"use client";

import { useCallback, useEffect, useState, type JSX } from "react";
import { CalendlyModal } from "./CalendlyModal";

/**
 * Makes every Calendly link on the site open in an inline dark modal instead
 * of a new browser tab.
 *
 * Rather than converting each CTA into a stateful button, this listens for
 * clicks in the capture phase at the document level and intercepts any anchor
 * whose href points at calendly.com. Calendly CTAs live in ~20 components in
 * several different shapes — plain `<a>`, `<Button asChild>`, CMS-driven href
 * strings passed down as props — and they all funnel through a rendered
 * anchor, so a single listener covers all of them and keeps new CTAs working
 * automatically.
 *
 * Opt out on a specific link with `data-no-calendly-modal`.
 */

const isCalendlyHref = (href: string): boolean => {
  try {
    // Match calendly.com and its subdomains only — a bare `endsWith` would
    // also match lookalikes such as `notcalendly.com`.
    const { hostname } = new URL(href, window.location.href);
    return hostname === "calendly.com" || hostname.endsWith(".calendly.com");
  } catch {
    return false;
  }
};

export const CalendlyModalProvider = (): JSX.Element => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      // Leave modified clicks alone — a deliberate "open in new tab" should
      // still do exactly that.
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = (event.target as HTMLElement | null)?.closest?.("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.hasAttribute("data-no-calendly-modal")) return;

      const href = anchor.getAttribute("href");
      if (!href || !isCalendlyHref(href)) return;

      event.preventDefault();
      setUrl(anchor.href);
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  const close = useCallback(() => setUrl(null), []);

  return <CalendlyModal url={url} onClose={close} />;
};
